<script setup>
import { computed, ref, watch, useTemplateRef } from 'vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useTeletelFeatures } from 'dashboard/composables/useTeletelFeatures';

import SearchInput from './SearchInput.vue';
import SearchFilters from './SearchFilters.vue';

const props = defineProps({
  initialQuery: { type: String, default: '' },
});

const emit = defineEmits(['search', 'filterChange']);

const filters = defineModel('filters', { type: Object, default: () => ({}) });

const searchInputRef = useTemplateRef('searchInputRef');
const searchQuery = ref(props.initialQuery);
const { isFeatureFlagEnabled } = usePolicy();
const { isTeletelFeatureEnabledOrLegacy } = useTeletelFeatures();

// Teletel supplies its own OpenSearch implementation on self-hosted Community
// installations. The account flag remains the operator-controlled switch, but
// the filters must not depend on Chatwoot's Enterprise/Cloud installation gate.
const showAdvancedFilters = computed(() =>
  isTeletelFeatureEnabledOrLegacy(
    'advanced_search',
    isFeatureFlagEnabled(FEATURE_FLAGS.ADVANCED_SEARCH)
  )
);

const onSearch = query => {
  if (query?.trim() && searchInputRef.value) {
    searchInputRef.value.addToRecentSearches(query.trim());
  }
  emit('search', query);
};

const onSelectRecentSearch = query => {
  searchQuery.value = query;
  onSearch(query);
};

watch(
  () => props.initialQuery,
  newValue => {
    if (searchQuery.value !== newValue) {
      searchQuery.value = newValue;
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col gap-2">
    <SearchInput
      ref="searchInputRef"
      v-model="searchQuery"
      @search="onSearch"
      @select-recent-search="onSelectRecentSearch"
    >
      <SearchFilters
        v-if="showAdvancedFilters"
        v-model="filters"
        class="w-full"
        @update-filters="$emit('filterChange', $event)"
      />
    </SearchInput>
  </div>
</template>
